import { lazy } from 'react';

// A dynamic import can fail permanently, not just transiently: a deploy replaces the
// content-hashed chunk filenames while a tab is still running the previous shell, so
// the URL that shell asks for no longer exists — and the SPA fallback answers the miss
// with index.html, which the browser rejects as a module. The service worker precaches
// every chunk (see vite.config.js), so a controlled tab still resolves the old name;
// an uncontrolled one (first visit, or the deliberately SW-free signup/admin hosts)
// would white-screen instead. Reload once to pick up the current index.html. The latch
// is session-scoped so it survives that reload and a genuinely broken build can't loop.
//
// Every lazy view goes through this (App's page roots and sign-in gates, the pages' Settings
// modal / KB manage view / consent page), so they share the one latch — including a failed
// on-demand English catalog, which withNamespaces (i18n.js) rejects with.
const RELOAD_LATCH = 'es_chunk_reload';

// The latch's catch path on its own, for a failed load outside a lazy view (the gear menu's
// on-demand catalog): reload once, or reject with `err` if this session already tried.
export function reloadOnce(err) {
  let alreadyTried = true; // storage blocked (private mode) ⇒ never auto-reload
  try {
    alreadyTried = window.sessionStorage.getItem(RELOAD_LATCH) === '1';
    if (!alreadyTried) window.sessionStorage.setItem(RELOAD_LATCH, '1');
  } catch { /* storage blocked */ }
  if (alreadyTried) return Promise.reject(err);
  window.location.reload();
  // Deliberately never settles: the document is already being torn down, and
  // resolving with a placeholder would paint a broken page for a frame first.
  return new Promise(() => {});
}

export function lazyPage(load) {
  return lazy(() => load().then((mod) => {
    // A chunk resolved, so this shell matches the server again — re-arm the latch for
    // whatever deploy lands later in the same session.
    try { window.sessionStorage.removeItem(RELOAD_LATCH); } catch { /* storage blocked */ }
    return mod;
  }).catch(reloadOnce));
}
